import { create } from "zustand";

/**
 * Deferred payout origination draft (send-side). Gallery-safe zustand store —
 * no persist middleware (seeded by useSeedMockData).
 */
export interface IOriginationDraft {
  sourceCountry: string;
  destinationCountry: string;
  sourceCurrency: string;
  destinationCurrency: string;
  sendAmount: number;
  totalDebit: number;
  fundingMethod: string;
  capabilityKey?: string;
  fundingPaymentMethodId?: string;
  recipientName: string;
}

export type IComposeDraft = Omit<IOriginationDraft, "recipientName">;

export interface IDeferredMintResult {
  claimUrl: string;
  originationId: string;
  expiresAt?: string;
  /** Sender first name read fresh at mint time; absent while CIP is pending. */
  senderFirstName?: string;
}

interface DeferredOriginationState {
  draft: IOriginationDraft | null;
  setDraft: (draft: IOriginationDraft | null) => void;

  composeDraft: IComposeDraft | null;
  setComposeDraft: (composeDraft: IComposeDraft | null) => void;

  pendingFundingMethod: string | null;
  setPendingFundingMethod: (method: string | null) => void;

  pendingRecipientName: string;
  setPendingRecipientName: (name: string) => void;

  mintResult: IDeferredMintResult | null;
  setMintResult: (result: IDeferredMintResult | null) => void;

  reset: () => void;
}

export const useDeferredOriginationStore = create<DeferredOriginationState>(
  (set) => ({
    draft: null,
    setDraft: (draft) => set(() => ({ draft })),
    composeDraft: null,
    setComposeDraft: (composeDraft) => set(() => ({ composeDraft })),
    pendingFundingMethod: null,
    setPendingFundingMethod: (pendingFundingMethod) =>
      set(() => ({ pendingFundingMethod })),
    pendingRecipientName: "",
    setPendingRecipientName: (pendingRecipientName) =>
      set(() => ({ pendingRecipientName })),
    mintResult: null,
    setMintResult: (mintResult) => set(() => ({ mintResult })),
    reset: () =>
      set(() => ({
        draft: null,
        composeDraft: null,
        mintResult: null,
        pendingFundingMethod: null,
        pendingRecipientName: "",
      })),
  })
);
